import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import WhiteBackgroundGuarantor from "../../assets/img/WhiteBackgroundGuarantor.svg";
import useTranslator from "../../hooks/useTranslator";
import {
  amlKycChecks,
  getIdentityDetails,
  haveValue,
  isDefaulter,
  isKycAmlDefaultApproved,
  KYC_TOOLTIP_PRIORITY,
  getKycConsiderResult,
  getKycDocumentStatus,
} from "../../helper/common";
import {
  getKybPeopleState,
  getKycScoreState,
} from "../../helper/profileCompletionScore";
import constants from "../../Utils/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ApplicationsModal from "./ApplicationsModal";
import NewApplicationModal from "./NewApplicationModal";
import ApplicationContext from "../../context/application/ApplicationContext";
import axiosInstance from "../../services/authWrapper";
import AuthContext from "../../context/auth/AuthContext";
import FullidAuthentication from "../Tenant/FullidAuthentication";
import { useMediaQuery } from "react-responsive";
import Summary from "../Summary";
import SkorTicketButton from "../SkorTicket/SkorTicketButton";
import FreeTextModal from "../Tenant/FreeTextModal";
import { Tooltip as ReactTooltip } from "react-tooltip";
import KycAmlCheckReportModal from "./KycAmlCheckReportModal";
import { getKybPeopleReasons, getKybReasons } from "../../helper/kybReasons";
import useServiceFile from "../../hooks/useServiceFile";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import TenantContext from "../../context/tenant/TenantContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import { isSkorSdkSearch } from "../../helper/skorSdk";
import { Avatar, Badge, Button, Card, Icon, Skeleton, Stat } from "../design";
import "./tenantDetailsHeader.css";

const TenantDetailsHeader = (props) => {
  // Company login tenant (landlord view): company name and the KYB /
  // signatories' KYC statuses instead of the individual stats.
  const isCompanyTenant = props?.isCompanyTenant === true;
  const { _t } = useTranslator();
  const isMobile = useMediaQuery({ maxWidth: 667 });
  const queryParameters = new URLSearchParams(window.location.search);
  const { workspaceId } = useParams();
  const guarantor_id = queryParameters.get("guarantor");
  let [isFirstTime, setIsFirstTime] = useState(
    queryParameters.get("welcome") ?? false
  );
  const [showModal, setShowModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(
    queryParameters.get("applications") === "true"
  );
  const [showNewApplicationModal, setShowNewApplicationModal] = useState(false);
  const [showKycPopup, setShowKycPopup] = useState(
    queryParameters.get("kyc") === "true"
  );
  const [showFreeTextPopup, setShowFreeTextPopup] = useState(false);
  const [isOpenSummary, setIsOpenSummary] = useState(false);
  const [showAmlKycChecks, setShowAmlKycChecks] = useState(false);

  const tenantContext = useContext(TenantContext);
  const { isSecWelcomePopup } = tenantContext;

  const landLordContext = useContext(LandLordContext);
  const { getProfileResolutionStatus } = landLordContext;
  const [kycResolution, setKycResolution] = useState(null);

  const [showAppWelcomePopup, setShowAppWelcomePopup] = useState(
    queryParameters.get("welcome") === "true" && isSecWelcomePopup
  );

  const { loadUser, user: originalUser } = useContext(AuthContext);
  const {
    myApplications,
    pendingApplications,
    applicationsLoading,
    loadApplicationsData,
    fetchLandlordTenantApplications,
  } = useContext(ApplicationContext);

  const [landlordTenantApps, setLandlordTenantApps] = useState([]);
  const [landlordTenantAppsLoading, setLandlordTenantAppsLoading] =
    useState(false);

  const {
    user = {},
    forensicAnalysis = null,
    userIncome = {},
    setShowDefaulterCheck,
    setInsurableModal,
    fullinfo,
    setUpdateSourceObj,
    handleUpdateSource,
    showReportIcons = true,
    setOpenGuarantorModel,
    isGuarantor,
    isFromReport,
    setShowChatBot,
    SetIsSentEmailDrawer,
    updateTenantData,
    loadFullInfo,
    llShowOnly,
  } = props;

  const loadKycResolutions = useCallback(async () => {
    if (!user?._id) return;
    if (!llShowOnly) return;
    try {
      const response = await getProfileResolutionStatus(user._id, "kyc");
      setKycResolution(response);
      return response;
    } catch (error) {
      console.log(error, "Error in getProfileResolutionStatus");
      setKycResolution(null);
    }
  }, [getProfileResolutionStatus, user?._id]);

  useEffect(() => {
    loadKycResolutions();
  }, [loadKycResolutions]);

  const isGuarantorUser = useMemo(() => user?.gurantor_for?.length > 0, [user]);

  const experianJson = user?.tenant_extra_field_id?.experianJson;
  const experianData =
    experianJson?.["s:Envelope"]?.["s:Body"]?.GenerarInformeResponse
      ?.GenerarInformeResult?.InformeDelphi4A3;
  const experianStatus = user?.tenant_extra_field_id?.experian?.status;
  const normalizedExperianStatus = experianStatus?.toLowerCase();
  // Experian commented condition
  const isExperianReportMissing = !haveValue(experianJson);
  // const isExperianReportMissing = false;
  const experianStatusLabel = (() => {
    if (normalizedExperianStatus === constants.PROCESSING) {
      return _t("processing", "Processing");
    }

    if (
      normalizedExperianStatus === constants.VERIFIED_DOCUMENT_NOT_FOUND ||
      normalizedExperianStatus === constants.FAILED
    ) {
      return _t("checkNotAvailable", "Check not available");
    }

    return _t("pending", "Pending");
  })();

  // Manual landlord resolutions keep precedence; otherwise KYC review or an
  // unresolved AML "consider" is shown as review.
  const kycAmlDisplayState = amlKycChecks(user, kycResolution)
    ? "approved"
    : getKycScoreState(user, kycResolution) === "review"
      ? "review"
      : "notApproved";
  const [kycDetails, setKycDetails] = useState(null);
  // Prefetch the KYC source file on profile load so the report modal
  // shows the "Verified with …" line instantly on open (cached in hook).
  const prefetchedKycFile = useServiceFile(user?._id, "KYC", true, isGuarantor);
  useEffect(() => {
    setKycDetails(getIdentityDetails(user, user?.onfido_user_kyc));
  }, [user]);

  useEffect(() => {
    if (!llShowOnly || !user?._id || props.reportMode) return;
    setLandlordTenantAppsLoading(true);
    const sharedId = queryParameters.get("sharedprofileid");
    fetchLandlordTenantApplications(sharedId)
      .then((apps) => setLandlordTenantApps(apps))
      .catch(() => setLandlordTenantApps([]))
      .finally(() => setLandlordTenantAppsLoading(false));
  }, [user?._id, llShowOnly]);

  useEffect(() => {
    if (queryParameters.get("applications") === "true") {
      const url = new URL(window.location.href);
      url.searchParams.delete("applications");
      window.history.replaceState({}, "", url.toString());
    }

    if (queryParameters.get("welcome") === "true") {
      const url = new URL(window.location.href);
      url.searchParams.delete("welcome");
      window.history.replaceState({}, "", url.toString());
    }

    const handleOpenApplications = () => setShowApplicationsModal(true);
    window.addEventListener("openApplicationsModal", handleOpenApplications);
    return () =>
      window.removeEventListener(
        "openApplicationsModal",
        handleOpenApplications
      );
  }, []);

  useEffect(() => {
    if (isSecWelcomePopup) {
      setShowAppWelcomePopup(true);
    }
  }, [isSecWelcomePopup]);

  const getEmploymentStatus = () => {
    if (user?.employment_status == constants.employed) {
      return _t("employed");
    }
    if (user?.employment_status == constants.self_employed) {
      return _t("selfEmployed");
    }
    if (user?.employment_status == constants.student) {
      return _t("student");
    }
    if (user?.employment_status == constants.unemployed) {
      return _t("unemployed");
    }
    if (user?.employment_status == constants.retired) {
      return _t("retired");
    }
    if (user?.employment_status == constants.public_servant) {
      return _t("publicServant");
    }
    if (user?.employment_status == constants.company) {
      return _t("company");
    }

    return _t("na");
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open KYC/AML modal when ?kycAml=true is present (from report "To resolve" click)
  // Do NOT clean URL here — keep param until modal closes to avoid early removal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const routerParams = new URLSearchParams(location.search);
    const hasKyc =
      params.get("kycAml") === "true" || routerParams.get("kycAml") === "true";
    if (hasKyc) {
      setShowAmlKycChecks(true);
    }
  }, [location.search]);

  const handleKycAmlClose = useCallback(() => {
    setShowAmlKycChecks(false);
    // Clean URL only when modal closes
    const url = new URL(window.location.href);
    if (url.searchParams.get("kycAml") === "true") {
      url.searchParams.delete("kycAml");
      window.history.replaceState({}, "", url.toString());
    }
    const rp = new URLSearchParams(location.search);
    if (rp.get("kycAml") === "true") {
      rp.delete("kycAml");
      const newSearch = rp.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng").toLowerCase() || "en-us"
  );

  useEffect(() => {
    setCurrentLang(localStorage.getItem("i18nextLng").toLowerCase() || "en-us");
  }, [localStorage.getItem("i18nextLng")]);

  const handleChangeSource = () => {
    handleUpdateSource();
    setUpdateSourceObj([
      {
        _id: userIncome?.all_sources?._id,
        all_sources: userIncome?.all_sources,
        current_selected: userIncome?.source,
        current_income_mode: userIncome?.source_mode,
        type: "tenant",
      },
    ]);
  };

  const updateUserSharerData = async (data) => {
    try {
      if (!haveValue(data?.is_edit)) {
        data.is_edit = true;
      }
      await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/users/profile/${user?._id}`,
        data,
        {
          headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
        }
      );
      loadUser("tenant");
    } catch (error) {
      console.log("Error updating user");
    }
  };

  const getReviewReason = () => {
    const kycUser = user?.onfido_user_kyc;
    const status = getKycDocumentStatus(user);
    const kycChecks = kycUser?.kyc_checks;

    if (
      status === "review" &&
      kycUser?.kyc_data?.document_type === "unidentified"
    ) {
      return llShowOnly
        ? _t(
            "onfidoUnidentifiedDocumentReviewLandlordMessage",
            "The tenant uploaded unsupported identification documents, so identity verification could not be completed successfully. Please ask the tenant to upload a valid government-issued ID with a visible photo. Copies or scanned documents are not accepted."
          )
        : _t(
            "onfidoUnidentifiedDocumentReviewTenantMessage",
            "Please upload a photo of a valid identification document that clearly shows your face. Copies or scanned documents are not accepted."
          );
    }

    if (!kycChecks) return _t("onfidoFallbackMessage");

    const isSanctions = getKycConsiderResult(
      kycChecks,
      "image_integrity.supported_document.properties.issuing_country_subject_to_sanctions"
    );

    if (isSanctions) {
      return _t("onfidoImageIntegritySanctionsMessage");
    }

    for (const { path, message, explanation } of KYC_TOOLTIP_PRIORITY) {
      if (getKycConsiderResult(kycChecks, path)) {
        if (path?.includes("image_quality")) {
          return `${_t(message)}: ${_t(explanation)}`;
        }
        return _t(message);
      }
    }

    return kycChecks.result === "consider" ? _t("onfidoFallbackMessage") : null;
  };

  const flaggedGuarantors =
    !llShowOnly &&
    Array.isArray(user?.tenant_extra_field_id?.selected_onboarding_users)
      ? user.tenant_extra_field_id.selected_onboarding_users?.filter(
          (item) => item?.type == "guarantor"
        )?.length
      : 0;

  const handleNavigateReport = () => {
    const sharedId =
      queryParameters.get("agencylink") ||
      queryParameters.get("sharedprofileid");
    let queryParam = haveValue(queryParameters.get("agencylink"))
      ? `agencylink=${sharedId}`
      : `sharedprofileid=${sharedId}`;
    if (haveValue(guarantor_id)) {
      queryParam += `&guarantor_id=${guarantor_id}`;
    }

    const redirectUrl = window.location.pathname + window.location.search;
    queryParam += `&redirectUrl=${encodeURIComponent(redirectUrl)}`;

    window.open(
      `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/${
        haveValue(workspaceId) ? workspaceId + "/" : ""
      }generatedReport/${sharedId}?${queryParam}`,
      "_blank"
    );
  };

  const IncomeTooltip = () => (
    <>
      <span
        className="tdh-info"
        data-tooltip-id="tenant-details-header-tooltip"
      >
        <Icon name="info" size={16} />
      </span>
      <ReactTooltip
        id="tenant-details-header-tooltip"
        place="left"
        noArrow
        className="react-tooltip-styling"
        render={() => (
          <p>
            {haveValue(userIncome?.method)
              ? _t(userIncome?.method)
              : _t("noIncomeSelected")}
          </p>
        )}
      />
    </>
  );

  const isInsurable = originalUser?.current_workspace?.insurance_service_status;

  useEffect(() => {
    if (isOpenSummary) {
      document.body.classList.add("profile-open");
    } else {
      document.body.classList.remove("profile-open");
    }

    return () => document.body.classList.remove("profile-open");
  }, [isOpenSummary]);

  const handleGoBack = () => {
    if (isSkorSdkSearch()) {
      navigate(-1);
      return;
    }

    const currentFullPath = location.pathname + location.search + location.hash;
    const fallbackPath = `/${workspaceId}/profile-request`;
    const maxAttempts = 5; // Prevent infinite loops
    let attempts = 0;

    const tryNavigateBack = () => {
      const currentIdx = window.history.state?.idx || 0;

      if (currentIdx === 0 || attempts >= maxAttempts) {
        // No more history or too many attempts
        navigate(fallbackPath, { replace: true });
        return;
      }

      navigate(-1);
      attempts++;

      setTimeout(() => {
        const newFullPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        if (newFullPath === currentFullPath) {
          // Still on same URL, try again
          tryNavigateBack();
        }
        // If URL changed, we're done (navigation succeeded)
      }, 50);
    };

    if (window.history.state?.idx > 0) {
      tryNavigateBack();
    } else {
      navigate(fallbackPath, { replace: true });
    }
  };

  const isProfileOutdated = !!(user?.profile_outdated || user?.is_outdated);

  const holderName = haveValue(forensicAnalysis?.Name?.dataInJSON?.name)
    ? forensicAnalysis.Name.dataInJSON.name
    : `${user?.fname ?? ""} ${user?.lname ?? ""}`;

  // Guarantors: what to show and when the box can be opened
  const guarantorForCount = user?.gurantor_for?.length ?? 0;
  const isGuarantorFor = guarantorForCount > 0 || isGuarantor;
  const addedGuarantors = +(
    user?.gurantor_added_id?.length + flaggedGuarantors ?? 0
  );
  const hasGuarantors =
    guarantorForCount > 0 ||
    (user?.gurantor_id?.length ?? 0) > 0 ||
    addedGuarantors > 0;
  const guarantorValue = isGuarantorFor
    ? guarantorForCount
    : (((llShowOnly
        ? user?.gurantor_added_id?.filter((item) =>
            ["processing", "present", "accepted"].includes(item.status)
          )?.length
        : user?.gurantor_added_id?.length) ?? 0) + flaggedGuarantors ?? 0);
  const canOpenGuarantors =
    (isMobile && !llShowOnly) || (showReportIcons && hasGuarantors);
  const openGuarantors = () => {
    if (isMobile && !llShowOnly) {
      if (addedGuarantors === 0) navigate("/guarantor/onboarding");
      else setOpenGuarantorModel(true);
    } else if (showReportIcons && hasGuarantors) {
      setOpenGuarantorModel(true);
    }
  };

  // Applications
  const applicationsCount = llShowOnly
    ? landlordTenantApps.length
    : myApplications.length;
  const applicationsAreLoading = llShowOnly
    ? landlordTenantAppsLoading
    : applicationsLoading;

  // Identity check flag shown next to the ID number (tenant side)
  // ID document result only (AML is shown on the landlord side).
  const kycWorkflowStatus = getKycDocumentStatus(user);
  const kycFlag =
    kycWorkflowStatus === "declined" || kycWorkflowStatus === "review"
      ? kycWorkflowStatus
      : null;

  const loadingValue = (
    <span role="status">
      <span className="ds-visually-hidden">{_t("loading")}</span>
      <Skeleton width={24} height={14} radius="pill" />
    </span>
  );

  const companyName =
    user?.onfido_user_kyc?.kyb_data?.company_name ||
    user?.company_legal_name ||
    user?.fname ||
    "";
  // Same badge as the "KYC & AML checks" stat; with a review / declined
  // reason, the same warning icon and tooltip as the tenant's KYC flag.
  const renderCompanyState = (state, reasons = [], tooltipId) => (
    <>
      {renderCompanyBadge(state)}
      {reasons.length > 0 && (
        <>
          <span
            className={`tdh-flag tdh-flag--${state === "declined" ? "declined" : "review"}`}
            data-tooltip-id={tooltipId}
          >
            <Icon name="warning" size={16} />
          </span>
          <ReactTooltip
            id={tooltipId}
            place="bottom"
            className="react-tooltip-styling"
            render={() => (
              <div>
                {reasons.map((reason) => (
                  <p key={reason}>{reason}</p>
                ))}
              </div>
            )}
          />
        </>
      )}
    </>
  );
  const renderCompanyBadge = (state) => (
    <Badge
      tone={
        state === "approved"
          ? "lime"
          : state === "review"
            ? "warning"
            : state === "pending"
              ? "neutral"
              : "danger"
      }
    >
      {_t(state)}
    </Badge>
  );

  return (
    <>
      <div
        className={`tenant-details-header ${!llShowOnly ? "profileTenant" : ""} ${
          isFromReport ? "profileinfoReport" : ""
        }`}
      >
        <Card as="div" className="tdh-card">
          <div className="tdh-lead">
            {showReportIcons && (llShowOnly || haveValue(guarantor_id)) && (
              <Button
                variant="outline"
                size="md"
                iconOnly
                iconStart={<Icon name="chevronLeft" />}
                aria-label={_t("back")}
                onClick={handleGoBack}
              />
            )}
            <Avatar
              size="lg"
              name={isCompanyTenant ? companyName : holderName}
            />
            {isCompanyTenant && (
              <div className="tdh-company">
                <span className="tdh-company__name">{companyName}</span>
                <Badge tone="neutral">
                  {_t(
                    "companyBadge",
                    "Company profile header: badge next to the company name"
                  )}
                </Badge>
              </div>
            )}
          </div>

          <div
            className={`tdh-stats ${isCompanyTenant ? "tdh-stats--company" : ""}`}
          >
            {isCompanyTenant ? (
              <>
                <Stat
                  icon={<Icon name="building" />}
                  label={_t(
                    "companyVerificationKyb",
                    "Company profile header: tile label for the Didit KYB business verification status"
                  )}
                  value={renderCompanyState(
                    getKycScoreState(user),
                    getKybReasons(user, _t, llShowOnly),
                    "tdh-kyb-reason"
                  )}
                />
                <Stat
                  icon={<Icon name="idCard" />}
                  label={_t(
                    "authorisedSignatoryKyc",
                    "Company profile header: tile label for the combined KYC status of all authorised signatories"
                  )}
                  value={renderCompanyState(
                    getKybPeopleState(user),
                    getKybPeopleReasons(user, _t, llShowOnly),
                    "tdh-signatory-reason"
                  )}
                />
              </>
            ) : (
              <>
                {isMobile && (
                  <Stat
                    icon={<Icon name="wallet" />}
                    label={_t(
                      "selectedAnnualIncome",
                      "On new Skor Lite tenantDetails page header"
                    )}
                    value={
                      <>
                        €{userIncome?.amount}
                        {showReportIcons &&
                          llShowOnly &&
                          haveValue(userIncome?.all_sources) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              iconStart={<Icon name="swap" size={16} />}
                              aria-label={_t("updateIncomeSource")}
                              onClick={handleChangeSource}
                            />
                          )}
                      </>
                    }
                  />
                )}

                {!llShowOnly && (
                  <Stat
                    icon={<Icon name="idCard" />}
                    label={
                      kycDetails?.type === "dni"
                        ? _t(
                            "dniNieDetails",
                            "On new Skor Lite tenantDetails page header"
                          )
                        : _t("passport")
                    }
                    value={
                      <>
                        {kycDetails?.value}
                        {kycFlag && (
                          <>
                            <span
                              className={`tdh-flag tdh-flag--${kycFlag}`}
                              data-tooltip-id="kyc-flag-tooltip"
                            >
                              <Icon name="warning" size={16} />
                            </span>
                            <ReactTooltip
                              id="kyc-flag-tooltip"
                              place="bottom"
                              className="react-tooltip-styling"
                              render={() => <p>{getReviewReason()}</p>}
                            />
                          </>
                        )}
                      </>
                    }
                    action={
                      haveValue(
                        user?.tenant_extra_field_id?.forensic_analysis
                          ?.profileReports
                      ) && (
                        <>
                          <span
                            data-tooltip-id={
                              isProfileOutdated
                                ? "redo-kyc-blocked-tooltip"
                                : undefined
                            }
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              iconStart={
                                <img
                                  src="/images/redoIcon.svg"
                                  alt=""
                                  className="tdh-btn-img"
                                />
                              }
                              disabled={isProfileOutdated}
                              onClick={() => {
                                if (!isProfileOutdated) setShowKycPopup(true);
                              }}
                            >
                              {_t("redo")}
                            </Button>
                          </span>
                          <ReactTooltip
                            id="redo-kyc-blocked-tooltip"
                            place="top"
                            className="react-tooltip-styling"
                            render={() =>
                              isProfileOutdated ? (
                                <p>
                                  {_t(
                                    "profileOutdatedRenewFirst",
                                    "Your profile is outdated. Please renew your profile first."
                                  )}
                                </p>
                              ) : null
                            }
                          />
                        </>
                      )
                    }
                  />
                )}

                <Stat
                  icon={<Icon name="briefcase" />}
                  label={_t("employmentStatus")}
                  value={getEmploymentStatus()}
                />

                {llShowOnly && haveValue(kycWorkflowStatus) && (
                  <Stat
                    icon={<Icon name="idCard" />}
                    label={_t("kycAndAmlChecks")}
                    onClick={() => setShowAmlKycChecks(true)}
                    value={
                      <Badge
                        tone={
                          kycAmlDisplayState === "approved"
                            ? "lime"
                            : kycAmlDisplayState === "review"
                              ? "warning"
                              : "danger"
                        }
                      >
                        {kycAmlDisplayState === "approved"
                          ? _t("approvedKycAml")
                          : kycAmlDisplayState === "review"
                            ? _t("reviewKycAml", "Under review")
                            : _t("notApprovedKycAMl")}
                        {/* #NOTE: if default is false but AML is approved, show ! */}
                        {!isKycAmlDefaultApproved(user)
                          ? amlKycChecks(user, kycResolution)
                            ? " !"
                            : null
                          : null}
                      </Badge>
                    }
                  />
                )}

                {llShowOnly && (
                  <Stat
                    icon={<Icon name="shield" />}
                    label={_t("experianCheck")}
                    onClick={() => setShowDefaulterCheck(true)}
                    value={
                      <Badge
                        tone={
                          isExperianReportMissing
                            ? "warning"
                            : isDefaulter(experianData)
                              ? "danger"
                              : "lime"
                        }
                      >
                        {isExperianReportMissing
                          ? experianStatusLabel
                          : _t(isDefaulter(experianData) ? "no" : "yes")}
                      </Badge>
                    }
                  />
                )}

                {llShowOnly && isInsurable && (
                  <Stat
                    icon={<Icon name="shieldCheck" />}
                    label={_t("insurable")}
                    onClick={
                      showReportIcons
                        ? () => setInsurableModal(true)
                        : undefined
                    }
                    value={
                      <Badge
                        tone={isDefaulter(experianData) ? "danger" : "lime"}
                      >
                        {_t(!isDefaulter(experianData) ? "yes" : "no")}
                      </Badge>
                    }
                  />
                )}

                <Stat
                  icon={<Icon name="users" />}
                  label={isGuarantorFor ? _t("guarantorFor") : _t("guarantors")}
                  onClick={canOpenGuarantors ? openGuarantors : undefined}
                  value={guarantorValue}
                  action={
                    !isGuarantor &&
                    !llShowOnly &&
                    guarantorForCount === 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="tdh-edit-btn"
                        iconStart={
                          <Icon
                            name={addedGuarantors === 0 ? "plus" : "edit"}
                            size={14}
                          />
                        }
                        onClick={
                          addedGuarantors === 0
                            ? () => navigate("/guarantor/onboarding")
                            : () => setOpenGuarantorModel(true)
                        }
                      >
                        {addedGuarantors === 0 ? _t("add") : _t("edit")}
                      </Button>
                    )
                  }
                />

                {!isGuarantor && guarantorForCount === 0 && (
                  <div className="tdh-stat-anchor">
                    {showAppWelcomePopup && (
                      <div className="welcome-popup applications-welcome-popup">
                        <div className="content-hover-div">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            className="tdh-popup-close"
                            iconStart={<Icon name="close" size={16} />}
                            aria-label={_t("close")}
                            onClick={() => setShowAppWelcomePopup(false)}
                          />
                          <p className="text-break contentText">
                            {_t("applicationSectionPopupMessage")}
                          </p>
                        </div>
                      </div>
                    )}
                    <Stat
                      icon={<Icon name="link" />}
                      label={
                        <>
                          {_t("applications")}
                          {!llShowOnly &&
                            pendingApplications.length > 0 &&
                            !applicationsLoading && (
                              <Badge tone="danger">
                                {pendingApplications.length}
                              </Badge>
                            )}
                        </>
                      }
                      onClick={() => setShowApplicationsModal(true)}
                      value={
                        applicationsAreLoading
                          ? loadingValue
                          : applicationsCount
                      }
                      action={
                        !llShowOnly && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="tdh-edit-btn"
                            iconStart={
                              <Icon
                                name={
                                  myApplications.length === 0 ? "plus" : "edit"
                                }
                                size={14}
                              />
                            }
                            onClick={() => setShowApplicationsModal(true)}
                          >
                            {_t("add")}
                          </Button>
                        )
                      }
                    />
                  </div>
                )}

                {!llShowOnly && (
                  <Stat
                    icon={<Icon name="message" />}
                    label={_t("aboutMe")}
                    value={
                      user?.about_me
                        ? user.about_me.length > 10
                          ? `${user.about_me.slice(0, 10)}...`
                          : user.about_me
                        : _t("noInformation")
                    }
                    action={
                      showReportIcons && (
                        <Button
                          variant="secondary"
                          size="sm"
                          iconStart={
                            <Icon
                              name={
                                (user?.about_me?.length ?? 0) === 0
                                  ? "plus"
                                  : "edit"
                              }
                              size={14}
                            />
                          }
                          onClick={() => setShowFreeTextPopup(true)}
                        >
                          {(user?.about_me?.length ?? 0) === 0
                            ? _t("add")
                            : _t("edit")}
                        </Button>
                      )
                    }
                  />
                )}
              </>
            )}
          </div>

          {llShowOnly && isInsurable && !isCompanyTenant && <IncomeTooltip />}

          {showReportIcons && llShowOnly && (
            <div className="tdh-actions">
              <Stat
                icon={<Icon name="fileText" />}
                label={_t("profileSummary")}
                value={_t("viewSummary")}
                onClick={() => setIsOpenSummary(true)}
              />
              {!props.reportMode && (
                <SkorTicketButton tenant={user} isGuarantor={isGuarantor} />
              )}
              {WORKSPACE_ROLES_CAPABILITIES?.tenants?.interactionRoles?.includes(
                originalUser?.current_workspace_role
              ) && (
                <Button
                  variant="outline"
                  size="md"
                  iconOnly
                  iconStart={<Icon name="send" size={16} />}
                  aria-label={_t("sendReport")}
                  title={_t("sendReport")}
                  onClick={() => SetIsSentEmailDrawer(true)}
                />
              )}
              <Button
                variant="primary"
                size="md"
                iconOnly
                iconStart={<Icon name="eye" size={18} />}
                aria-label={_t("viewReport")}
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_SKOR_LITE_URL}${currentLang}/report/${user?._id}?env=dev`,
                    "_blank"
                  )
                }
              />
              {WORKSPACE_ROLES_CAPABILITIES?.tenants?.generateReport?.includes(
                originalUser?.current_workspace_role
              ) && (
                <Button
                  variant="primary"
                  size="md"
                  iconOnly
                  iconStart={<Icon name="download" size={18} />}
                  aria-label={_t("downloadReport")}
                  onClick={handleNavigateReport}
                />
              )}
            </div>
          )}
        </Card>
        <Summary
          user={user}
          showReportIcons={showReportIcons}
          setShowChatBot={setShowChatBot}
          llShowOnly={llShowOnly}
          isShowOnMobile={true}
          isOpen={isOpenSummary}
          setIsOpen={setIsOpenSummary}
        />
      </div>
      {/* #NOTE: hide applications modal for guarantor */}
      {!isGuarantorUser && (
        <ApplicationsModal
          isOpen={showApplicationsModal}
          onClose={() => setShowApplicationsModal(false)}
          applications={
            llShowOnly
              ? landlordTenantApps
              : [
                  ...pendingApplications.map((a) => ({
                    ...a,
                    isPendingInvitation: true,
                  })),
                  ...myApplications,
                ]
          }
          loading={llShowOnly ? landlordTenantAppsLoading : applicationsLoading}
          onNewApplication={() => setShowNewApplicationModal(true)}
          onApplicationsChange={loadApplicationsData}
          llShowOnly={llShowOnly}
        />
      )}
      <NewApplicationModal
        isOpen={showNewApplicationModal}
        onClose={() => {
          setShowNewApplicationModal(false);
          setShowApplicationsModal(true);
        }}
        onSuccess={loadApplicationsData}
      />
      {showKycPopup && (
        <FullidAuthentication
          user={user}
          isGuarantor={isGuarantor}
          usedFor={"popup"}
          setShowKycPopup={setShowKycPopup}
          hideBackButton={true}
        />
      )}
      {showFreeTextPopup && (
        <FreeTextModal
          user={user}
          showFreeTextPopup={showFreeTextPopup}
          setShowFreeTextPopup={setShowFreeTextPopup}
          updateTenantData={updateTenantData}
          loadFullInfo={loadFullInfo}
          isPopup={true}
        />
      )}

      {showAmlKycChecks && (
        <KycAmlCheckReportModal
          isOpen={showAmlKycChecks}
          onClose={handleKycAmlClose}
          user={user}
          resolutions={kycResolution}
          onResolutionChange={loadKycResolutions}
          serviceFile={prefetchedKycFile}
          isGuarantor={isGuarantor}
        />
      )}
    </>
  );
};

export default TenantDetailsHeader;
